import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import DashboardLayout from '../../components/DashboardLayout';
import { Skeleton } from '../../components/Skeleton';
import SeverityBadge from '../../components/SeverityBadge';
import Icon from '../../components/Icon';
import { formatDateTime } from '../../lib/time';

export default function AdminMonitoring() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminMonitoringOverview().then(setData).finally(() => setLoading(false));
    const id = setInterval(() => api.adminMonitoringOverview().then(setData).catch(() => {}), 20000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <DashboardLayout title={t('adminMon.overview_title')} heading={false}>
        <Skeleton className="sk-block" style={{ marginTop: 20 }} />
      </DashboardLayout>
    );
  }

  const a = data?.agents || { total: 0, active: 0, silent: 0, never: 0 };

  return (
    <DashboardLayout title={t('adminMon.overview_title')} heading={false}>
      <h1 className="page-title">{t('adminMon.overview_title')}</h1>
      <p className="page-sub">{t('adminMon.overview_sub')}</p>

      <div className="stat-row">
        <div className="card stat">
          <div className="stat-label"><Icon name="activity" size={13} /> {t('adminMon.agents_active')}</div>
          <div className="stat-value">{a.active}<span style={{ fontSize: '0.9rem', color: 'var(--text-3)', fontWeight: 400 }}> / {a.total}</span></div>
          <div className="stat-meta">
            {a.silent > 0 && <span className="down">{t('adminMon.agents_silent_n', { n: a.silent })}</span>}
            {a.silent === 0 && <span>{t('adminMon.all_ok')}</span>}
          </div>
        </div>
        <div className="card stat">
          <div className="stat-label"><Icon name="alert-triangle" size={13} /> {t('adminMon.alerts_24h')}</div>
          <div className="stat-value">{data?.alerts24h ?? 0}</div>
          <div className="stat-meta">{t('adminMon.all_customers')}</div>
        </div>
        <div className="card stat">
          <div className="stat-label"><Icon name="users" size={13} /> {t('adminMon.active_subs')}</div>
          <div className="stat-value">{data?.activeSubscriptions ?? 0}</div>
          <div className="stat-meta">{t('adminMon.paying')}</div>
        </div>
        {data?.health?.avg != null && (
          <div className="card stat">
            <div className="stat-label"><Icon name="gauge" size={13} /> {t('adminMon.avg_health')}</div>
            <div className="stat-value" style={{ color: data.health.avg >= 80 ? 'var(--ok)' : data.health.avg >= 55 ? 'var(--warn)' : 'var(--crit)' }}>
              {data.health.avg}
            </div>
            <div className="stat-meta">
              {t('adminMon.health_dist', {
                ok: data.health.distribution?.ok || 0,
                warn: data.health.distribution?.warn || 0,
                crit: data.health.distribution?.crit || 0,
              })}
            </div>
          </div>
        )}
      </div>

      {a.never > 0 && (
        <div className="badge" style={{ marginBottom: 'var(--sp-4)' }}>
          <Icon name="download" size={12} /> {t('adminMon.never_n', { n: a.never })} — <Link to="/admin/agents">{t('adminMon.nav_agents')}</Link>
        </div>
      )}

      <div className="card" style={{ padding: 0, marginTop: 'var(--sp-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--sp-4)', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: '0.98rem' }}>{t('adminMon.recent_alerts')}</h3>
          <Link to="/dashboard/alerts" className="btn btn-subtle btn-sm">{t('dashboard.view_all')}</Link>
        </div>
        {(!data?.recentAlerts || data.recentAlerts.length === 0) ? (
          <p style={{ color: 'var(--text-3)', fontSize: 13, padding: 'var(--sp-4)', margin: 0 }}>{t('dashboard.no_alerts')}</p>
        ) : (
          <div className="list">
            {data.recentAlerts.map((al) => (
              <div className="list-row" key={al.id} style={{ padding: 'var(--sp-4)' }}>
                <SeverityBadge severity={al.severity} />
                <div className="row-main">
                  <div style={{ fontWeight: 500, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {t(`metric.${al.type}`, al.type)}
                    {al.customer && (
                      <Link to={`/admin/customers/${al.customer.id || ''}`} style={{ fontSize: 12, color: 'var(--text-3)' }}>
                        · {al.customer.displayName || al.customer.email}
                      </Link>
                    )}
                    {al.serverLabel && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>· {al.serverLabel}</span>}
                  </div>
                  <div style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{al.message}</div>
                </div>
                <div className="row-time">{formatDateTime(al.triggeredAt, lang)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
